// The signed-in app shell: the top bar is mounted ONCE here and stays mounted
// while you move between Meetings and Calls.
//
// Previously each page rendered its own TopBar, so switching sections unmounted
// and remounted it — and the Calls page additionally gated it behind its own
// api.me() fetch, so the bar visibly disappeared and came back. A layout route
// removes the whole class of bug: the chrome never re-renders on navigation,
// and the session is fetched once for the whole shell.

import { useCallback, useEffect, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router';

import { api, Me } from '../api';
import { TopBar } from './TopBar';

type Session = { kind: 'loading' } | { kind: 'anonymous' } | { kind: 'user'; me: Me };

/** What the shell hands its pages. `me === null` means anonymous. */
export interface ShellContext {
  me: Me | null;
}

export function AppShell() {
  const [session, setSession] = useState<Session>({ kind: 'loading' });

  useEffect(() => {
    api
      .me()
      .then((me) => setSession({ kind: 'user', me }))
      .catch(() => setSession({ kind: 'anonymous' }));
  }, []);

  const onSignedOut = useCallback(() => setSession({ kind: 'anonymous' }), []);

  if (session.kind === 'loading') return <div className="bz-state-page">Loading…</div>;
  // Anonymous: no chrome — the page itself decides (landing, or a redirect).
  if (session.kind === 'anonymous') {
    return <Outlet context={{ me: null } satisfies ShellContext} />;
  }
  return (
    <div className="bz-app">
      <TopBar me={session.me} onSignedOut={onSignedOut} />
      <main className="bz-app-main">
        <Outlet context={{ me: session.me } satisfies ShellContext} />
      </main>
    </div>
  );
}

/** Read the shell's session from a page rendered inside it. */
export function useShell(): ShellContext {
  return useOutletContext<ShellContext>();
}
